<template>
  <!--
    Tailwind UI components require Tailwind CSS v1.8 and the @tailwindcss/ui plugin.
    Read the documentation to get started: https://tailwindui.com/documentation
  -->
  <div class="relative inline-block text-left">
    <div @click="updateShow()">
      <span class="rounded-md shadow-sm">
        <button type="button"
                class="inline-flex justify-center w-full rounded-md border border-gray-300 px-4 py-2 bg-white text-base leading-5 font-medium text-gray-700 hover:text-gray-500 focus:outline-none focus:border-blue-300 focus:ring-blue-200 active:bg-gray-50 active:text-gray-800"
                id="options-menu" aria-haspopup="true" aria-expanded="true">
          Navigate
          <!-- Heroicon name: chevron-down -->
          <svg class="-mr-1 ml-2 h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
            <path fill-rule="evenodd"
                  d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                  clip-rule="evenodd"/>
          </svg>
        </button>
      </span>
    </div>

    <!--
      Dropdown panel, show/hide based on dropdown state.

      Entering: "transition ease-out duration-100"
        From: "transform opacity-0 scale-95"
        To: "transform opacity-100 scale-100"
      Leaving: "transition ease-in duration-75"
        From: "transform opacity-100 scale-100"
        To: "transform opacity-0 scale-95"
    -->
    <transition
        enter-active-class="transition ease-out duration-100"
        enter-class="transform opacity-0 scale-95"
        enter-to-class="transform opacity-100 scale-100"
        leave-active-class="transition ease-in duration-75"
        leave-class="transform opacity-100 scale-100"
        leave-to-class="transform opacity-0 scale-95"
    >
      <div v-show="show"
           @click="handleDropdownClick"
           @mousedown.middle="handleMiddleClick"
           class="z-30 origin-top-right absolute right-0 mt-2 w-56 rounded-md shadow-lg">
        <div class="rounded-md bg-white ring-1 ring-black ring-opacity-5" role="menu" aria-orientation="vertical" aria-labelledby="options-menu">
          <div v-if="hasSites" class="py-1">
            <a :href="company.buildCompanyRoute('alarms', routeCompany)"
               class="group flex items-center px-4 py-2 text-base leading-5 text-gray-700 plain-link hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus:bg-gray-100 focus:text-gray-900"
               role="menuitem">
              <span class="mr-4 pl-1 h-5 w-5 lnr icon-alarm2" style="padding-top: 0.15em; font-size: 1rem;"></span>
              Alarms
            </a>
          </div>
          <div v-if="hasSites" class="border-t border-gray-100"></div>
          <div v-if="hasSites" class="py-1">
            <a :href="company.buildCompanyRoute('reports', routeCompany)"
               class="group flex items-center px-4 py-2 text-base leading-5 text-gray-700 plain-link hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus:bg-gray-100 focus:text-gray-900"
               role="menuitem">
              <span class="mr-4 pl-1 h-5 w-5 lnr icon-clipboard-empty" style="padding-top: 0.15em; font-size: 1rem;"></span>
              Reports
            </a>
            <a :href="company.buildCompanyRoute('site-admin/calibration-info', routeCompany)"
               class="group flex items-center px-4 py-2 text-base leading-5 text-gray-700 plain-link hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus:bg-gray-100 focus:text-gray-900"
               role="menuitem">
              <span class="mr-4 pl-1 h-5 w-5 lnr icon-calendar-check" style="padding-top: 0.15em; font-size: 1rem;"></span>
              Calibrations
            </a>
          </div>
          <div v-if="hasSites" class="border-t border-gray-100"></div>
          <div v-if="hasSites" class="py-1">
            <a :href="company.buildCompanyRoute('settings', routeCompany)"
               class="group flex items-center px-4 py-2 text-base leading-5 text-gray-700 plain-link hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus:bg-gray-100 focus:text-gray-900"
               role="menuitem">
              <span class="mr-4 pl-1 h-5 w-5 lnr icon-cog" style="padding-top: 0.15em; font-size: 1rem;"></span>
              Settings
            </a>
          </div>
          <div v-if="hasSites" class="border-t border-gray-100"></div>
          <div class="py-1">
            <a href="https://www.sonicu.com/academy"
               target="_blank"
               class="group flex items-center px-4 py-2 text-base leading-5 text-gray-700 plain-link hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus:bg-gray-100 focus:text-gray-900"
               role="menuitem">
              <span class="mr-4 pl-1 h-5 w-5 lnr icon-graduation-hat" style="padding-top: 0.15em; font-size: 1rem;"></span>
              Sonicu Academy
            </a>
            <a v-if="hasSites"
               :href="company.buildCompanyRoute('support', routeCompany)"
               class="group flex items-center px-4 py-2 text-base leading-5 text-gray-700 plain-link hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus:bg-gray-100 focus:text-gray-900"
               role="menuitem">
              <span class="mr-4 pl-1 h-5 w-5 lnr icon-telephone" style="padding-top: 0.15em; font-size: 1rem;"></span>
              Support
            </a>
          </div>
          <div class="border-t border-gray-100"></div>
          <div class="py-1">
            <a v-if="hasMultipleSites"
               href="/sites"
               class="group flex items-center px-4 py-2 text-base leading-5 text-gray-700 plain-link hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus:bg-gray-100 focus:text-gray-900"
               role="menuitem">
              <span class="mr-4 pl-1 h-5 w-5 lnr icon-map-marker" style="padding-top: 0.15em; font-size: 1rem;"></span>
              Sites
            </a>
            <a v-if="canReadUsers"
               :href="usersRoute"
               class="group flex items-center px-4 py-2 text-base leading-5 text-gray-700 plain-link hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus:bg-gray-100 focus:text-gray-900"
               role="menuitem">
              <span class="mr-4 pl-1 h-5 w-5 lnr icon-users" style="padding-top: 0.15em; font-size: 1rem;"></span>
              Users
            </a>
            <a v-if="hasSites"
               :href="profileRoute"
               class="group flex items-center px-4 py-2 text-base leading-5 text-gray-700 plain-link hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus:bg-gray-100 focus:text-gray-900"
               role="menuitem">
              <span class="mr-4 pl-1 h-5 w-5 lnr icon-user" style="padding-top: 0.15em; font-size: 1rem;"></span>
              My Profile
            </a>
            <div v-if="permissions.isSonicu() && isEnterpriseRoute"
                 class="group flex items-center px-4 py-2 text-base leading-5 text-gray-700 plain-link hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus:bg-gray-100 focus:text-gray-900"
                 role="menuitem"
                 @click.stop="toggleDemoMode">
              <span class="mr-4 pl-1 h-5 w-5 lnr" :class="demoMode ? 'icon-camera-play' : 'icon-camera-crossed'" style="padding-top: 0.15em; font-size: 1rem;"></span>
              <span>{{ demoMode ? 'Demo Mode Active' : 'Demo Mode Off' }}</span>
            </div>
            <a href="/logout"
               class="group flex items-center px-4 py-2 text-base leading-5 text-gray-700 plain-link hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus:bg-gray-100 focus:text-gray-900"
               role="menuitem">
              <span class="mr-4 pl-1 h-5 w-5 lnr icon-exit-right" style="padding-top: 0.15em; font-size: 1rem;"></span>
              Logout
            </a>
          </div>
        </div>
      </div>
    </transition>
  </div>

</template>

<script>
import {SonicuCoreMixin} from "@/mixins/SonicuCoreMixin";

export default {
  name: "NavigateDropdown",
  mixins: [SonicuCoreMixin],
  props:{
    hasCalibration: {
      type: Boolean,
      default: true,
    },
    hasSites: {
      type: Boolean,
      default: false,
    },
    hasMultipleSites: {
      type: Boolean,
      default: false,
    },
    onMultisitePage: {
      type: Boolean,
      default: false,
    },
    baseRoute: {
      type: String,
      default: '',
    },
    baseCompany: {
      type: String,
      default: null,
    },
    canReadUsers: {
      type: Boolean,
      default: true,
    },
    canEditSelf: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      show: false,
      demoMode: false,
      nonCompanyRoutes: [
        'sites',
        'enterprise-dashboard',
        'SonicuTools',
        'scripts',
        'probe-calibration',
        'device-association',
        'shipping-checklist',
      ],
    }
  },
  mounted() {
    this.demoMode = localStorage.getItem('enterprise_demo_mode') === 'true'
  },
  methods: {
    updateShow(show = null) {
      if (!show) {
        show = !this.show
      }
      this.show = show
      this.$emit('navigate-dropdown', this.show)
    },
    handleDropdownClick(event) {
      // Only close dropdown if the click is not on a link (which would be middle-click)
      if (event.target.tagName !== 'A' && !event.target.closest('a')) {
        this.updateShow(false)
      }
    },
    handleMiddleClick(event) {
      // For middle-click, we want to close the dropdown but allow the link to open in new tab
      this.updateShow(false)
      // Don't prevent default here - let the browser handle the middle-click on links
        window.open(event.target.href, '_blank')
    },
    toggleDemoMode(event) {
      event.stopPropagation()
      this.demoMode = !this.demoMode
      localStorage.setItem('enterprise_demo_mode', this.demoMode ? 'true' : 'false')
      window.dispatchEvent(new CustomEvent('demo-mode-changed', {detail: this.demoMode}))
    }
  },
  computed: {
    usersRoute() {
      let companyId = this.company.getCompanyId()
      if (this.nonCompanyRoutes.indexOf(companyId) !== -1) {
        return this.baseRoute + 'users'
      }
      return this.company.buildCompanyRoute('users', this.routeCompany)
    },
    profileRoute() {
      let companyId = this.company.getCompanyId()
      return this.company.buildCompanyRoute('my-profile', this.routeCompany)
    },
    routeCompany() {
      return this.company.getCompanyId() === 'sonicu'
          ? this.baseCompany
          : this.company.getCompanyId()
    },
    isEnterpriseRoute() {
      return window.location.href.includes('enterprise-dashboard')
    },
  }
}
</script>

<style scoped>

</style>